import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import axios from "axios"
import { toast } from "react-toastify"

import { GlobalWorkerOptions, getDocument } from "pdfjs-dist/legacy/build/pdf"
import * as pdfjsLib from "pdfjs-dist/build/pdf"
import jsQR from "jsqr"
import { version } from "pdfjs-dist"

import "react-resizable/css/styles.css"
import "../../Book/book.css"
import "../style.css"

import config from "../../../Helpers/config.json"
import { updateOrInsertBookStats, updateOrInsertBookTranslationChecked } from "../../../Helpers/statsApiService"
import DashboardNavbar from "../../DashboardNavbar/DashbaordNavbar"
import CongratulationModal from "../congratulationModal/congratulation"
import PasswordConfirmation from "../../PasswordConfirmation/PasswordConfirmation"
import ReplayAndPlayButton from "../../../utils/replayAndPlayButton"
import BookPagination from "../../../utils/bookPagination"
import TranslationPopup from "../../../utils/translationPopup"
import LanguagePopup from "../../../utils/languagePopup"
import DictionaryPopup from "../../../utils/dictionaryPopup"

import donut_dark from "../../../assets/dashboard/donut.png"
import donut_dark_bottom from "../../../assets/dashboard/Donut-light-big.png"

GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.js`

const BookDetails = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const bookData = location.state?.SelectedBook || null

  // Memoized URL parameters and language detection
  const { userLanguage, langParam, destLang } = useMemo(() => {
    const queryParams = new URLSearchParams(location.search)
    const lang = queryParams.get("lang") || "english"

    const langMap = {
      urdu: "ur", hindi: "hi", spanish: "es",
      german: "de", arabic: "ar", english: "en"
    }

    return {
      userLanguage: lang,
      langParam: lang,
      destLang: langMap[lang?.toLowerCase()] || "en"
    }
  }, [location.search])

  // State management
  const [pagesData, setPagesData] = useState({})
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [loadingProgress, setLoadingProgress] = useState(0)
  const [allLanguagesSupport, setAllLanguagesSupport] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [allStory, setAllStory] = useState([])
  const [showStory, setShowStory] = useState({})
  const [readData, setReadData] = useState("")
  const [endingIndex, setEndingIndex] = useState(0)
  const [highlightedText, setHighlightedText] = useState("")
  const [isToolTipLoading, setIsToolTipLoading] = useState(false)
  const [apiResponse, setAPIResponse] = useState({})
  const [translatedText, setTranslatedText] = useState({})
  const [highlightPosition, setHighlightPosition] = useState({ top: 0, left: 0 })
  const [selectedText, setSelectedText] = useState("")
  const [isDictionaryOpen, setDictionaryOpen] = useState(false)
  const [isLanguageOpen, setLanguageOpen] = useState(false)
  const [translationOpen, setTranslationOpen] = useState(false)
  const [audioUrl, setAudioUrl] = useState("")
  const [audioLoading, setAudioLoading] = useState(false)
  const [timeSpent, setTimeSpent] = useState(0)
  const [isTimeSpent, setIsTimeSpent] = useState(0)
  const [selectedLanguage, setSelectedLanguage] = useState("French")
  const [audioSource, setAudioSource] = useState(undefined)
  const [showCongrateModal, setShowCongrateModal] = useState(false)
  const [valueCheck, setValueCheck] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [backgroundMusicVolume, setBackgroundMusicVolume] = useState(0.2)
  const [buttonState, setButtonState] = useState({ listen: true, pause: false, resume: false })
  const [currentWordIndex, setCurrentWordIndex] = useState(0)
  const [words, setWords] = useState([])

  // Refs
  const utteranceRef = useRef(null)
  const substringsRef = useRef([])
  const audioPlayerRef = useRef()
  const mainAudioRef = useRef(null)
  const mainAudioResRef = useRef(null)
  const backgroundMusicRef = useRef(null)
  const backgroundMusicEngRef = useRef(null)
  const wordTimingRef = useRef(null)
  const pdfCacheRef = useRef(new Map())
  const translationCacheRef = useRef(new Map())

  // Constants
  const languageCodeMap = useMemo(() => ({
    english: "en", french: "fr", chinese: "zh-CN",
    germany: "de", hindi: "hi", urdu: "ur"
  }), [])

  const backgroundMusicUrl = "https://tts.azure.fictiongenie.com/audio/180126-ambient-morning-chill-with-birds-21348_FinalUrdu.mp3"

  // Memoized voice mapping
  const voiceMapping = useMemo(() => ({
    french: "Google français",
    hindi: "Google हिन्दी",
    chinese: "Google 普通话（中国大陆）",
    german: "Google Deutsch",
    urdu: "Google हिन्दी",
    default: "Google UK English Female"
  }), [])

  // Optimized handlers
  const handleVolumeChange = useCallback((event) => {
    const volume = parseFloat(event.target.value)
    setBackgroundMusicVolume(volume)
    if (backgroundMusicRef.current) {
      backgroundMusicRef.current.volume = volume
    }
  }, [])

  const congratulationModalOpen = useCallback(() => {
    if (timeSpent >= 10) {
      updateOrInsertBookStats(localStorage.getItem("id"), localStorage.profileId, bookData.id, currentPage + 2)
      setShowCongrateModal(true)
    } else {
      navigate("/dashboard")
    }
  }, [timeSpent, bookData, currentPage, navigate])

  const nextPage = useCallback(() => {
    setAudioUrl(null)
    againPlay()
    if (currentPage !== allStory.length - 1) {
      if (timeSpent >= 10) {
        updateOrInsertBookStats(localStorage.getItem("id"), localStorage.profileId, bookData.id, currentPage + 2)
      }
      setCurrentPage(prev => prev + 1)
      setIsTimeSpent(prev => prev + 1)
    }
  }, [currentPage, allStory.length, timeSpent, bookData])

  const prevPage = useCallback(() => {
    setAudioUrl(null)
    againPlay()
    if (currentPage !== 0) {
      setCurrentPage(prev => prev - 1)
    }
  }, [currentPage])

  const setupWordHighlighting = useCallback((text) => {
    const wordArray = text.split(' ').filter(word => word.trim() !== '')
    setWords(wordArray)
    setCurrentWordIndex(0)
  }, [])

  const startWordHighlighting = useCallback(() => {
    if (!mainAudioResRef.current || words.length === 0) return

    const audio = mainAudioResRef.current

    const startHighlighting = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        const totalDuration = audio.duration
        const wordsPerSecond = words.length / totalDuration

        wordTimingRef.current = setInterval(() => {
          if (audio.paused || audio.ended) {
            clearInterval(wordTimingRef.current)
            return
          }

          const currentTime = audio.currentTime
          const expectedWordIndex = Math.floor(currentTime * wordsPerSecond)

          if (expectedWordIndex >= 0 && expectedWordIndex < words.length && expectedWordIndex !== currentWordIndex) {
            setCurrentWordIndex(expectedWordIndex)
          }
        }, 50)
      } else {
        setTimeout(startHighlighting, 100)
      }
    }

    startHighlighting()
  }, [words, currentWordIndex])

  const pauseWordHighlighting = useCallback(() => {
    if (wordTimingRef.current) {
      clearInterval(wordTimingRef.current)
      wordTimingRef.current = null
    }
  }, [])

  const stopWordHighlighting = useCallback(() => {
    if (wordTimingRef.current) {
      clearInterval(wordTimingRef.current)
      wordTimingRef.current = null
    }
    setCurrentWordIndex(0)
  }, [])

  const playBook = useCallback(async () => {
    setAudioLoading(true)
    const text = pagesData[currentPage].text
    const ageGroup = "Less than 6 years"
    const age = ageGroup.match(/\d+/)
    const fileName = `Grade${age}_${userLanguage}_${bookData?.id}_${bookData.title}_${currentPage}`
    const language = "en-US-Emma:DragonHDLatestNeural"
    const voice = "en-US-Emma:DragonHDLatestNeural"

    try {
      const response = await axios.post(
        "https://azuretts.fictiongenie.com/generate-speech",
        { text, fileName, language, voice },
        {
          headers: { "Content-Type": "application/json" },
          onDownloadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent
            const percentage = Math.round((loaded * 100) / total)
            console.log("Audio download progress:", percentage)
          }
        }
      )

      if (response.status === 200) {
        const data = response.data
        if (data.path) {
          setAudioLoading(false)
          const audioUrl = `https://azuretts.fictiongenie.com/audio/${fileName}.mp3`
          setAudioUrl(audioUrl)

          setTimeout(() => {
            if (mainAudioResRef.current) {
              mainAudioResRef.current.load()
              mainAudioResRef.current.play().catch(err => {
                console.error("Error auto-playing audio after loading:", err)
              })
            }
          }, 500)
        }
      } else {
        setAudioLoading(false)
      }
    } catch (error) {
      console.error("Error in playBook API call:", error)
      setAudioLoading(false)
    }
  }, [pagesData, currentPage, userLanguage, bookData])

  const fetchAndProcessPdf = useCallback(async (url) => {
    try {
      setLoadingProgress(0)
      const response = await axios.get(url, {
        responseType: 'arraybuffer',
        onDownloadProgress: progressEvent => {
          const { loaded, total } = progressEvent
          const percentage = Math.round((loaded * 100) / total)
          setLoadingProgress(Math.min(percentage, 80))
        }
      })

      const loadingPdfDocument = getDocument({ data: response.data })
      const pdfDocumentInstance = await loadingPdfDocument.promise
      const totalNumPages = pdfDocumentInstance.numPages
      const pages = totalNumPages - 1
      setTotalPages(pages)

      setLoadingProgress(90)
      await loadPageData(currentPage, pdfDocumentInstance)
      setLoadingProgress(100)
      setIsLoading(false)
    } catch (error) {
      console.error("Error fetching PDF:", error)
      setIsLoading(false)
    }
  }, [currentPage])

  const onSpeakText = useCallback((index, data, initialCase = false) => {
    if (initialCase) {
      return setHighlightedText(data)
    }
    setHighlightedText(e => e ? `${e}.${data}` : `${data}`)
  }, [])

  const generalCall = useCallback(() => {
    if (buttonState.listen) {
      speakText()
      setButtonState({ ...buttonState, listen: false, pause: true })
    } else if (buttonState.pause) {
      pauseSpeech()
      setButtonState({ ...buttonState, pause: false, resume: true })
    } else if (buttonState.resume) {
      resumeSpeech()
      setButtonState({ ...buttonState, pause: true, resume: false })
    }
  }, [buttonState])

  const againPlay = useCallback(() => {
    window.speechSynthesis.cancel()
    onSpeakText(0, "", true)
    setReadData(readData)
    setButtonState({
      listen: true,
      pause: false,
      resume: false,
    })
  }, [readData, onSpeakText])

  const speakText = useCallback((data = "") => {
    if ("speechSynthesis" in window) {
      const text = pagesData[currentPage]?.text
      if (!text) return

      if (userLanguage === "hindi") {
        substringsRef.current = text.split(",")
      } else if (userLanguage === "chinese") {
        substringsRef.current = text.split(/[。！？]/).filter(sentence => sentence.trim() !== "")
      } else if (userLanguage === "urdu") {
        substringsRef.current = text.split(/۔|\?|!/).filter(sentence => sentence.trim() !== "")
      } else {
        substringsRef.current = text.split(/[.!?]/).filter(sentence => sentence.trim() !== "")
      }
      playNext(0)
    }
  }, [pagesData, currentPage, userLanguage])

  const playNext = useCallback((index) => {
    backgroundMusicEngRef?.current?.play()
    if (index >= substringsRef.current.length) {
      setHighlightedText("")
      setButtonState(prev => ({ ...prev, pause: false, listen: true }))
      return
    }

    let substring = substringsRef.current[index]
    if (substring.endsWith("") || substring.endsWith("\"") || substring.endsWith("'")) {
      substring = substring.slice(0, -1)
    }

    utteranceRef.current = new SpeechSynthesisUtterance(substring)

    utteranceRef.current.onstart = () => {
      onSpeakText(index + 1, substring)
    }

    utteranceRef.current.onend = () => {
      playNext(index + 1)
    }

    const voices = window.speechSynthesis.getVoices()
    const voiceName = voiceMapping[userLanguage] || voiceMapping.default
    const customVoice = voices.find(voice => voice.name === voiceName)

    utteranceRef.current.voice = customVoice
    utteranceRef.current.rate = 1.0
    window.speechSynthesis.speak(utteranceRef.current)
  }, [onSpeakText, voiceMapping, userLanguage])

  const pauseSpeech = useCallback(() => {
    backgroundMusicEngRef?.current?.pause()
    if (utteranceRef.current) {
      window.speechSynthesis.pause()
    }
  }, [])

  const resumeSpeech = useCallback(() => {
    backgroundMusicEngRef?.current?.play()
    if (utteranceRef.current) {
      if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
        window.speechSynthesis.resume()
      } else {
        againPlay()
        playNext(0)
      }
    }
  }, [againPlay, playNext])

  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection()
    const text = selection?.toString().trim()

    if (text) {
      const range = selection.getRangeAt(0)
      const rect = range.getBoundingClientRect()
      const position = {
        top: rect.top - 150,
        left: rect.left + window.scrollX + rect.width - 70,
      }

      setHighlightPosition(position)
      setSelectedText(text)

      if (isDictionaryOpen) {
        handleCloseDictionary()
      }

      if (isLanguageOpen) {
        handleCloseLanguage()
        handleDoneLanguageClose()
      }
    }
  }, [isDictionaryOpen, isLanguageOpen])

  const countrySelect = useCallback((countryName) => {
    if (!selectedText) {
      toast.warn("Please select text", {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      })
      return
    }

    const languageCode = languageCodeMap[countryName]
    if (!languageCode) return

    setIsToolTipLoading(true)

    const formatData = JSON.parse(localStorage.getItem("data"));
    const authToken = formatData?.token;

    axios
      .post(`${config["baseUrl3"]}/translate`, {
        text: selectedText,
        dest: languageCode,
      }, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      })
      .then(function (response) {
        setTranslatedText(response.data.translated_text)
        setIsToolTipLoading(false)
        updateOrInsertBookTranslationChecked(
          localStorage.getItem("id"),
          localStorage.profileId,
          bookData.id,
          1,
          languageCode,
          selectedText,
          response.data.translated_text
        )
      })
      .catch(function (error) {
        console.log(error)
        setIsToolTipLoading(false)
      })
  }, [selectedText, languageCodeMap, bookData])

  const handleCloseDictionary = useCallback(() => {
    setDictionaryOpen(false)
    setHighlightPosition({ top: 0, left: 0 })
  }, [])

  const handleCloseLanguage = useCallback(() => {
    setLanguageOpen(false)
    setHighlightPosition({ top: 0, left: 0 })
  }, [])

  const handleSelectLanguage = useCallback((language) => {
    setSelectedLanguage(language)
  }, [])

  const handleDoneLanguage = useCallback((lang) => {
    setTranslationOpen(true)
    countrySelect(lang)
  }, [countrySelect])

  const handleDoneLanguageClose = useCallback(() => {
    setTranslationOpen(false)
  }, [])

  const addAlphaChannelToUint8ClampedArray = useCallback((unit8Array, imageWidth, imageHeight) => {
    const newImageData = new Uint8ClampedArray(imageWidth * imageHeight * 4)
    for (let j = 0, k = 0, jj = imageWidth * imageHeight * 4; j < jj;) {
      newImageData[j++] = unit8Array[k++]
      newImageData[j++] = unit8Array[k++]
      newImageData[j++] = unit8Array[k++]
      newImageData[j++] = 255
    }
    return newImageData
  }, [])

  const getPageImages = useCallback(async (pageNum, pdfDocumentInstance) => {
    try {
      const pdfPage = await pdfDocumentInstance.getPage(pageNum)
      const operatorList = await pdfPage.getOperatorList()
      const validObjectTypes = [
        pdfjsLib.OPS.paintImageXObject,
        pdfjsLib.OPS.paintImageXObjectRepeat,
        pdfjsLib.OPS.paintJpegXObject,
      ]

      const imagePromises = operatorList.fnArray.map((element, idx) => {
        if (validObjectTypes.includes(element)) {
          return new Promise((resolve) => {
            const imageName = operatorList.argsArray[idx][0]
            pdfPage.objs.get(imageName, async (image) => {
              const imageUnit8Array = image.data
              const imageWidth = image.width
              const imageHeight = image.height
              const imageUint8ArrayWithAlphaChannel =
                addAlphaChannelToUint8ClampedArray(
                  imageUnit8Array,
                  imageWidth,
                  imageHeight
                )
              const imageData = new ImageData(
                imageUint8ArrayWithAlphaChannel,
                imageWidth,
                imageHeight
              )
              const canvas = document.createElement("canvas")
              canvas.width = imageWidth
              canvas.height = imageHeight
              const ctx = canvas.getContext("2d")
              ctx.putImageData(imageData, 0, 0)
              const imageSrc = canvas.toDataURL()
              const decodedData = jsQR(
                imageUint8ArrayWithAlphaChannel,
                imageWidth,
                imageHeight
              )
              resolve({
                imageSrc,
                decodedData: decodedData ? decodedData.data : null,
              })
            })
          })
        }
      })

      const images = await Promise.all(imagePromises)
      return {
        images: images.filter(Boolean),
      }
    } catch (error) {
      console.log(error)
      return {
        images: [],
      }
    }
  }, [addAlphaChannelToUint8ClampedArray])

  const loadPageData = useCallback(async (pageNum, pdfDocumentInstance) => {
    const pageImages = await getPageImages(pageNum, pdfDocumentInstance)
    const pageText = await extractText(pageNum, pdfDocumentInstance)

    setPagesData(prevPagesData => ({
      ...prevPagesData,
      [pageNum]: {
        ...pageImages,
        ...pageText,
      },
    }))
    setReadData(pageText)
  }, [getPageImages])

  const translateText = useCallback(async (text, destLang, srcLang, pageNum) => {
    const cacheKey = `${text}-${destLang}-${srcLang}-${pageNum}`
    if (translationCacheRef.current.has(cacheKey)) {
      return translationCacheRef.current.get(cacheKey)
    }

    try {
      const langMap = {
        ur: "urdu", hi: "hindi", es: "spanish",
        de: "german", ar: "arabic", en: "english"
      }
      const book_read_lang = langMap[destLang?.toLowerCase()] || "english"

      const formatData = JSON.parse(localStorage.getItem("data"));
      const authToken = formatData?.token;

      const response = await axios.post(
        `${config["bookTranslationUrl"]}`,
        {
          text: text,
          src: srcLang,
          dest: destLang,
          book_id: bookData.id,
          book_name: bookData.title,
          book_lang: book_read_lang,
          book_page_no: pageNum,
          profile_id: localStorage.profileId
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`
          }
        }
      )

      if (response.status === 200) {
        const result = response.data
        const translatedContent = result.data.translatedContent || text
        translationCacheRef.current.set(cacheKey, translatedContent)
        return translatedContent
      } else {
        console.error("Translation API responded with an error:", response.statusText)
        return text
      }
    } catch (error) {
      console.error("Error in translation API call:", error)
      return text
    }
  }, [bookData])

  const extractText = useCallback(async (pageNum, pdfDocumentInstance) => {
    try {
      const page = await pdfDocumentInstance.getPage(pageNum)
      const textContent = await page.getTextContent()
      const pageText = textContent.items.map((item) => item.str).join(" ")

      let translatedText = pageText
      if (langParam !== "english") {
        translatedText = await translateText(pageText, destLang, "en", pageNum)
      }
      return { text: translatedText }
    } catch (error) {
      return { text: "" }
    }
  }, [langParam, destLang, translateText])

  // Memoized word rendering for performance
  const renderedWords = useMemo(() =>
    words.map((word, index) => (
      <span
        key={index}
        style={{
          backgroundColor: index === currentWordIndex ? "rgba(135, 206, 235, 0.4)" : "transparent",
          color: index === currentWordIndex ? "#ffffff" : "inherit",
          padding: "2px 4px",
          borderRadius: "4px",
          transition: "all 0.3s ease",
          boxShadow: index === currentWordIndex ? "0 2px 8px rgba(135, 206, 235, 0.3)" : "none"
        }}
      >
        {word}{" "}
      </span>
    )), [words, currentWordIndex]
  )

  // Effects with proper cleanup
  useEffect(() => {
    const startIndex = pagesData?.[currentPage]?.text?.split(".").join("").indexOf(highlightedText)
    const endIndex = startIndex + highlightedText?.length - 1
    setEndingIndex(endIndex)
  }, [highlightedText, showStory, pagesData, currentPage])

  useEffect(() => {
    if ((currentPage === 0 || currentPage === 4) && timeSpent === 10) {
      setValueCheck(true)
      updateOrInsertBookStats(
        localStorage.getItem("id"),
        localStorage.profileId,
        bookData.id,
        currentPage + 2
      )
    }
  }, [timeSpent, currentPage, bookData])

  useEffect(() => {
    setAudioSource(audioPlayerRef.current)
  }, [audioPlayerRef, audioSource])

  useEffect(() => {
    const startTime = Date.now()
    const interval = setInterval(() => {
      const elapsedTime = Math.floor((Date.now() - startTime) / 1000)
      setTimeSpent(elapsedTime)
    }, 1000)
    return () => clearInterval(interval)
  }, [currentPage])

  useEffect(() => {
    const loadAdjacentPages = async () => {
      const url = bookData?.pdfs?.[0]?.pdfPath
      if (url && totalPages > 0 && !isLoading) {
        const cacheKey = url
        let pdfDocumentInstance = pdfCacheRef.current.get(cacheKey)

        if (!pdfDocumentInstance) {
          pdfDocumentInstance = await getDocument(url).promise
          pdfCacheRef.current.set(cacheKey, pdfDocumentInstance)
        }

        if (!pagesData[currentPage]) {
          await loadPageData(currentPage, pdfDocumentInstance)
        }

        if (currentPage < totalPages && !pagesData[currentPage + 1]) {
          await loadPageData(currentPage + 1, pdfDocumentInstance)
        }
      }
    }

    loadAdjacentPages()
  }, [currentPage, bookData, totalPages, pagesData, loadPageData, isLoading])

  useEffect(() => {
    if (bookData?.pdfs?.[0]?.pdfPath) {
      fetchAndProcessPdf(bookData?.pdfs?.[0]?.pdfPath)
    }
  }, [bookData, fetchAndProcessPdf])

  useEffect(() => {
    if (pagesData[currentPage]?.text && userLanguage === "english") {
      setupWordHighlighting(pagesData[currentPage].text)
    }
  }, [currentPage, pagesData, userLanguage, setupWordHighlighting])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wordTimingRef.current) {
        clearInterval(wordTimingRef.current)
      }
      window.speechSynthesis.cancel()
    }
  }, [])

  return (
    <div className="new-dashboard-wrapper">
      {isLoading && (
        <div className="loader-overlay">
          <div className="loader" />
          <span className="loading-text">{`✨ Story loading...  ${loadingProgress}%! ✨`}</span>
        </div>
      )}
      <div className="dashbaord-donut-container">
        <img src={donut_dark} className="dashbaord-donut" alt="" />
      </div>
      <DashboardNavbar
        ageGroup={bookData?.age_group}
        selectedText={selectedText}
        bookData={bookData}
        userLanguage={userLanguage}
        setShowModal={setShowModal}
      />
      <img src={donut_dark_bottom} className="dashbaord-donut-bottom" alt="" />
      <div className="content-wrrapers">
        <div className="sub-content-wrrapers" id="sub-content-wraps">
          <div
            className="book-main-wraps book-container"
            style={{ background: `url(${pagesData[currentPage]?.images?.[0]?.imageSrc})` }}
          >
            <div className="left">
              <div className="book-detail-wrap-sub">
                <div className="left-top">
                  <div className="book-header-main">
                    <div className="book-name-writer-responsive">
                      <h1 className="head-story"> {bookData?.title}</h1>
                      <div className="story-writter-container">
                        <p className="story-wriiter">{pagesData[currentPage]?.chapter}</p>
                      </div>
                      <ReplayAndPlayButton
                        userLanguage={userLanguage}
                        audioLoading={audioLoading}
                        playBook={playBook}
                        audioUrl={audioUrl}
                        mainAudioRef={mainAudioRef}
                        againPlay={againPlay}
                        generalCall={generalCall}
                        buttonState={buttonState}
                        mainAudioResRef={mainAudioResRef}
                        backgroundMusicRef={backgroundMusicRef}
                        backgroundMusicVolume={backgroundMusicVolume}
                        backgroundMusicUrl={backgroundMusicUrl}
                        handleVolumeChange={handleVolumeChange}
                        startWordHighlighting={startWordHighlighting}
                        stopWordHighlighting={stopWordHighlighting}
                        pauseWordHighlighting={pauseWordHighlighting}
                      />
                    </div>
                    <div className="img-container mt-0 img-container-responsive">
                      <div className="dashboard-img-book-cover mt-0 book-cover-responsive">
                        <img
                          className="dashboard-img dashboard-img-responsive"
                          src={pagesData[currentPage]?.images?.[0]?.imageSrc}
                          alt=""
                        />
                        <div className="book-spine"></div>
                      </div>
                    </div>
                  </div>
                  <div className="head-story-responsive">
                    <h1 className="head-story"> {bookData?.title}</h1>
                    <div className="story-writter-container">
                      <p className="story-wriiter">{pagesData[currentPage]?.chapter}</p>
                    </div>
                  </div>
                  <div>
                    {bookData?.pdfs?.length === 0 ? (
                      <div className="not-found-story"><p>No content found!</p></div>
                    ) : (
                      <p
                        className={userLanguage === "urdu" ? "text-story text-story-urdu" : "text-story"}
                        onMouseUp={handleTextSelection}
                      >
                        {userLanguage === "english" && audioUrl ? (
                          renderedWords
                        ) : (
                          <>
                            <span style={{ backgroundColor: "#ffffff30" }}>{highlightedText}{" "}</span>
                            {!!highlightedText.length
                              ? pagesData[currentPage]?.text?.slice(endingIndex + 2)
                              : pagesData[currentPage]?.text?.slice(endingIndex + 1)
                            }
                          </>
                        )}
                        {highlightPosition.top !== 0 && (
                          <>
                            {isDictionaryOpen && (
                              <DictionaryPopup
                                highlightPosition={highlightPosition}
                                selectedText={selectedText}
                                userLanguage={userLanguage}
                                apiResponse={apiResponse}
                                handleCloseDictionary={handleCloseDictionary}
                              />
                            )}
                            {isLanguageOpen && !translationOpen && (
                              <LanguagePopup
                                highlightPosition={highlightPosition}
                                allLanguagesSupport={allLanguagesSupport}
                                selectedLanguage={selectedLanguage}
                                handleSelectLanguage={handleSelectLanguage}
                                handleCloseLanguage={handleCloseLanguage}
                                handleDoneLanguage={handleDoneLanguage}
                              />
                            )}
                            {translationOpen && (
                              <TranslationPopup
                                highlightPosition={highlightPosition}
                                selectedLanguage={selectedLanguage}
                                selectedText={selectedText}
                                isToolTipLoading={isToolTipLoading}
                                translatedText={translatedText}
                                handleDoneLanguageClose={handleDoneLanguageClose}
                              />
                            )}
                          </>
                        )}
                      </p>
                    )}
                  </div>
                  <BookPagination
                    currentPage={currentPage}
                    prevPage={prevPage}
                    totalPages={totalPages}
                    nextPage={nextPage}
                    congratulationModalOpen={congratulationModalOpen}
                  />
                </div>
                <CongratulationModal
                  showCongrateModal={showCongrateModal}
                  setShowCongrateModal={setShowCongrateModal}
                />
              </div>
            </div>
          </div>
          <div className="background-container"></div>
        </div>
      </div>
      <PasswordConfirmation
        showModal={showModal}
        setShowModal={setShowModal}
        route="/account"
      />
    </div>
  )
}

export default BookDetails